import type { RefObject } from "react";

import ProcessEnvironment from "@/constants/ProcessEnvironment";
import type {
  IncomingWebViewEvent,
  OutgoingWebViewEvent,
} from "@/constants/WebView";
import type {
  WebView,
  WebViewMessageEvent,
} from "@/crossPlatform/WebView/types";
import { isWeb } from "@/utils/platform.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";

interface WebViewMessage {
  payload?: unknown;
  type: IncomingWebViewEvent | OutgoingWebViewEvent;
}

const webViewMessageEventToWebViewMessage = (
  messageEvent: WebViewMessageEvent,
): undefined | WebViewMessage => {
  try {
    return JSON.parse(messageEvent.nativeEvent.data);
  } catch {
    return undefined;
  }
};

const postWebViewMessage = ({
  message,
  webViewRef,
}: {
  message: WebViewMessage;
  webViewRef?: RefObject<null | WebView>;
}) => {
  if (!webViewRef) {
    return;
  }
  if (!isWeb()) {
    webViewRef.current?.postMessage(JSON.stringify(message));
    return;
  }

  // `react-native-web-webview`'s `postMessage(message, origin)` forwards to
  // `iframe.contentWindow.postMessage(message, origin)`, which REQUIRES a
  // targetOrigin; thus the WebAppURL is used here
  const targetOrigin = ProcessEnvironment.WebAppURL;
  if (!targetOrigin) return;
  webViewRef.current?.postMessage(JSON.stringify(message), targetOrigin);
};

// non-web code should not be inspecting web origin
const isValidWebOrigin = (origin?: string) =>
  isWeb() &&
  isNotNilNotEmpty(origin) &&
  origin === ProcessEnvironment.WebAppURL;

export type { WebViewMessage };
export {
  isValidWebOrigin,
  postWebViewMessage,
  webViewMessageEventToWebViewMessage,
};
